import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { joinCsv, splitCsv } from '../../mixins/form-utils';

export interface OptionGroup {
  label: string;
  options: OptionValue[];
}

export interface OptionItem {
  label: string;
  value: string;
}

export type OptionValue = string | OptionItem;

@Component({
  selector: 'app-option-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './option-modal.component.html',
  styleUrl: './option-modal.component.css',
})
export class OptionModalComponent implements OnChanges {
  @Input() open = false;
  @Input() title = 'Select options';
  @Input() options: string[] = [];
  @Input() optionGroups: OptionGroup[] = [];
  @Input() currentValue = '';
  @Input() singleSelect = false;
  @Input() hint = '';
  @Output() closed = new EventEmitter<void>();
  @Output() applied = new EventEmitter<string>();

  selected = new Set<string>();
  filter = '';

  get filteredOptions(): string[] {
    const term = this.filter.trim().toLowerCase();
    if (!term) return this.options;
    return this.options.filter((option) => option.toLowerCase().includes(term));
  }

  get filteredOptionGroups(): OptionGroup[] {
    const term = this.filter.trim().toLowerCase();
    if (!this.optionGroups.length) return [];
    if (!term) return this.optionGroups;
    return this.optionGroups
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => this.optionLabel(option).toLowerCase().includes(term)),
      }))
      .filter((group) => group.options.length);
  }

  get hasOptions(): boolean {
    return this.optionGroups.length ? this.filteredOptionGroups.some((group) => group.options.length) : this.filteredOptions.length > 0;
  }

  ngOnChanges(): void {
    if (this.open) {
      this.selected = new Set(splitCsv(this.currentValue));
      this.filter = '';
    }
  }

  toggle(option: OptionValue): void {
    const value = this.optionValue(option);
    if (this.singleSelect) {
      this.applied.emit(value);
      this.closed.emit();
      return;
    }
    if (this.isConditionOption(value) && !this.selected.has(value)) {
      this.conditionOptions().forEach((condition) => this.selected.delete(condition));
    }
    if (this.selected.has(value)) this.selected.delete(value);
    else this.selected.add(value);
  }

  optionLabel(option: OptionValue): string {
    return typeof option === 'string' ? option : option.label;
  }

  optionValue(option: OptionValue): string {
    return typeof option === 'string' ? option : option.value;
  }

  clear(): void {
    this.selected.clear();
  }

  apply(): void {
    this.applied.emit(joinCsv(Array.from(this.selected), this.optionOrder()));
    this.closed.emit();
  }

  private optionOrder(): string[] {
    return this.optionGroups.length
      ? this.optionGroups.flatMap((group) => group.options.map((option) => this.optionValue(option)))
      : this.options;
  }

  private conditionOptions(): string[] {
    return this.optionGroups.find((group) => group.label.toLowerCase() === 'condition')?.options.map((option) => this.optionValue(option)) || [];
  }

  private isConditionOption(option: string): boolean {
    return this.conditionOptions().includes(option);
  }
}
